import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

async function headersAuth() {
  const token = await getAuthToken();
  return { Authorization: `Bearer ${token}` };
}

/** Mirrors server taskChatService.aggregateMessageStatus for plain receipt objects. */
function aggregateOutgoingStatus(deliveryReceipts, readReceipts, recipientsExcludingSender) {
  const to = recipientsExcludingSender || [];
  const getR = (id) =>
    readReceipts &&
    (readReceipts[id] ?? readReceipts[String(id)]);
  const getD = (id) =>
    deliveryReceipts &&
    (deliveryReceipts[id] ?? deliveryReceipts[String(id)]);

  if (to.length === 0) return "sent";

  if (to.every((id) => Boolean(getR(String(id))))) return "read";
  if (to.every((id) => Boolean(getD(String(id))))) return "delivered";

  return "sent";
}

function mergeReadOntoMessages(messages, { readerId, readAt, viewerId, memberIds }) {
  const rStr = String(readerId);

  return (messages || []).map((msg) => {
    if (msg.kind === "system") return msg;

    const senderStr = msg.senderId != null ? String(msg.senderId) : "";
    if (!senderStr || senderStr === rStr) return msg;

    const readReceipts = { ...(msg.readReceipts || {}), [rStr]: readAt };
    let next = { ...msg, readReceipts };

    const fromSelf = !!(viewerId && senderStr === String(viewerId));
    if (fromSelf && Array.isArray(memberIds) && memberIds.length > 0) {
      const recipientsExcludingSender = memberIds.filter((mid) => String(mid) !== senderStr);
      const deliveryReceipts = msg.deliveryReceipts || {};
      next = {
        ...next,
        fromMe: true,
        status: aggregateOutgoingStatus(deliveryReceipts, readReceipts, recipientsExcludingSender),
      };
    }

    return next;
  });
}

function mutateTaskChatReadReceipt(state, payload) {
  const { taskId, readerId, readAt, viewerId } = payload || {};
  if (!taskId || readerId == null || !readAt) return;

  const bucket = state.byTaskId[taskId];
  if (!bucket?.messages?.length) return;

  const memberIds = bucket.memberIds || [];
  bucket.messages = mergeReadOntoMessages(bucket.messages, {
    readerId,
    readAt,
    viewerId,
    memberIds,
  });
}

function recalcTotalUnread(items) {
  return (items || []).reduce((sum, item) => sum + (item.unreadCount || 0), 0);
}

function buildChatListPreview(envelope) {
  const type = envelope.messageType || "text";
  if (type === "image") return "Photo";
  if (type === "video") return "Video";
  if (type === "audio") return "Audio message";
  if (type === "document") return envelope.documentFileName || "Document";
  const body = String(envelope.body || "").trim();
  return body.length > 80 ? body.slice(0, 80) + "…" : body;
}

/** Page size aligned with inbox-style thread loading (latest window first). */
export const TASK_CHAT_PAGE_SIZE = 50;

export const fetchTaskChatMessages = createAsyncThunk(
  "taskChat/fetchMessages",
  async ({ taskId, limit = TASK_CHAT_PAGE_SIZE, offset = 0 }, { rejectWithValue }) => {
    try {
      const headers = await headersAuth();
      const res = await axios.get(`${SERVER_DOMAIN}/api/tasks/${taskId}/chat`, {
        headers,
        params: { limit, offset },
      });
      return { taskId, ...res.data.data };
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

/** Load older messages (offset = number already loaded counting from newest). */
export const fetchOlderTaskChatMessages = createAsyncThunk(
  "taskChat/fetchOlder",
  async ({ taskId }, { getState, rejectWithValue }) => {
    const bucket = getState().taskChat?.byTaskId?.[taskId];
    if (!bucket?.hasMore) {
      return rejectWithValue("no-more");
    }
    const offset = bucket.messages?.length ?? 0;
    try {
      const headers = await headersAuth();
      const res = await axios.get(`${SERVER_DOMAIN}/api/tasks/${taskId}/chat`, {
        headers,
        params: { limit: TASK_CHAT_PAGE_SIZE, offset },
      });
      return { taskId, ...res.data.data };
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const sendTaskChatMessage = createAsyncThunk(
  "taskChat/send",
  async ({ taskId, message, messageType = "text", tempId, documentFileName }, { rejectWithValue }) => {
    try {
      const headers = await headersAuth();
      const res = await axios.post(
        `${SERVER_DOMAIN}/api/tasks/${taskId}/chat/messages`,
        { message, messageType, tempId, documentFileName },
        { headers }
      );
      return { taskId, message: res.data.data.message };
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

/** Upload file for task chat (Tasks.View + task membership enforced server-side). Same validation as task media. */
export const uploadTaskChatMedia = createAsyncThunk(
  "taskChat/upload",
  async ({ taskId, file }, { rejectWithValue }) => {
    try {
      const headers = await headersAuth();
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${SERVER_DOMAIN}/api/tasks/${taskId}/chat/upload`, fd, {
        headers,
      });
      return { taskId, ...res.data.data };
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const markTaskChatReadThunk = createAsyncThunk(
  "taskChat/markRead",
  async ({ taskId }, { rejectWithValue, getState }) => {
    try {
      const headers = await headersAuth();
      const res = await axios.post(`${SERVER_DOMAIN}/api/tasks/${taskId}/chat/read`, {}, { headers });

      const s = getState();
      const viewerId =
        s.auth?.user?._id ||
        s.auth?.user?.id ||
        s.userDetails?.userDetails?._id ||
        s.userDetails?.userDetails?.id ||
        "";

      const readAt = res.data?.data?.readAt || new Date().toISOString();

      return {
        taskId,
        readerId: String(viewerId),
        readAt,
        viewerId: String(viewerId),
      };
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

export const TASK_CHAT_LIST_PAGE_SIZE = 50;

export const fetchTaskChatList = createAsyncThunk(
  "taskChat/fetchList",
  async ({ page = 1, limit = TASK_CHAT_LIST_PAGE_SIZE, search = "" } = {}, { rejectWithValue }) => {
    try {
      const headers = await headersAuth();
      const res = await axios.get(`${SERVER_DOMAIN}/api/tasks/chats/list`, {
        headers,
        params: { page, limit, search },
      });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

/** Loads the next page of the chat list and appends it (infinite scroll / "More" button). */
export const loadMoreTaskChatList = createAsyncThunk(
  "taskChat/loadMoreList",
  async (_, { getState, rejectWithValue }) => {
    const chatList = getState().taskChat?.chatList;
    const pagination = chatList?.pagination;
    if (!pagination?.hasMore) return rejectWithValue("no-more");
    try {
      const headers = await headersAuth();
      const nextPage = (pagination.page || 1) + 1;
      const res = await axios.get(`${SERVER_DOMAIN}/api/tasks/chats/list`, {
        headers,
        params: { page: nextPage, limit: pagination.limit || TASK_CHAT_LIST_PAGE_SIZE, search: chatList.search || "" },
      });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

const enrichForViewer = (raw, viewerId, memberIds = []) => {
  if (!raw) return raw;
  const senderStr = raw.senderId != null ? String(raw.senderId) : "";
  const fromMe = !!(viewerId && senderStr && String(viewerId) === senderStr);
  const deliveryReceipts = raw.deliveryReceipts || {};
  const readReceipts = raw.readReceipts || {};
  const next = {
    ...raw,
    deliveryReceipts,
    readReceipts,
    fromMe,
  };

  if (fromMe) {
    const recipientsExcludingSender = (memberIds || []).filter((mid) => String(mid) !== senderStr);
    next.status = aggregateOutgoingStatus(deliveryReceipts, readReceipts, recipientsExcludingSender);
  }

  return {
    ...next,
  };
};

const slice = createSlice({
  name: "taskChat",
  initialState: {
    byTaskId: {},
    chatList: {
      items: [],
      loading: false,
      loadingMore: false,
      error: null,
      selectedTaskId: null,
      totalUnread: 0,
      search: "",
      pagination: { page: 1, limit: TASK_CHAT_LIST_PAGE_SIZE, total: 0, hasMore: false },
    },
  },
  reducers: {
    clearTaskChat(state, action) {
      const id = action.payload;
      if (id && state.byTaskId[id]) delete state.byTaskId[id];
    },

    taskChatSocketEnvelope(state, action) {
      const { viewerId, taskId, envelope } = action.payload || {};
      if (!taskId || !envelope?.id || !viewerId) return;

      const prev = state.byTaskId[taskId] || {};
      state.byTaskId[taskId] = {
        ...prev,
        messages: prev.messages || [],
        memberIds: prev.memberIds || [],
        memberCount: prev.memberCount || 0,
        mentionMembers: prev.mentionMembers || [],
      };

      const list = [...(state.byTaskId[taskId].messages || [])];
      const memberIds = state.byTaskId[taskId].memberIds || [];
      const msg = envelope.kind === "system" ? { ...envelope } : enrichForViewer(envelope, viewerId, memberIds);

      const i = list.findIndex((x) => String(x.id) === String(msg.id));
      if (i !== -1) {
        list[i] = msg;
      } else {
        list.push(msg);
      }

      state.byTaskId[taskId].messages = list;
    },

    /** Apply read receipt broadcast without refetching the whole thread (`task_chat:message_status`). */
    taskChatReadReceiptBatch(state, action) {
      mutateTaskChatReadReceipt(state, action.payload);
    },

    /** Set which task chat is currently open in the Task Chats tab. */
    setSelectedTaskChatId(state, action) {
      state.chatList.selectedTaskId = action.payload || null;
    },

    /**
     * Called from socket handler when a new task_chat:message arrives.
     * Updates the chat list item: move to top, update preview, increment unread if not selected.
     */
    taskChatListSocketUpdate(state, action) {
      const { taskId, envelope, viewerId } = action.payload || {};
      if (!taskId || !envelope?.id) return;
      if (envelope.kind !== "user") return;

      const fromOther = envelope.senderId && viewerId && String(envelope.senderId) !== String(viewerId);

      const items = state.chatList.items;
      const idx = items.findIndex((item) => item.taskId === taskId);
      if (idx === -1) {
        // Task not in the currently-loaded page — list itself refreshes on next mount/load-more,
        // but the badge should still reflect it since `totalUnread` is now server-authoritative
        // across the full scope, not just what's loaded.
        if (fromOther) {
          state.chatList.totalUnread = (state.chatList.totalUnread || 0) + 1;
        }
        return;
      }

      const item = { ...items[idx] };
      item.lastMessage = buildChatListPreview(envelope);
      item.lastMessageAt = envelope.timestamp || new Date().toISOString();
      item.lastMessageType = envelope.messageType || "text";
      item.lastMessageSenderName = envelope.senderName || null;

      // Only increment unread for messages from others when this chat is NOT currently selected
      const isSelected = state.chatList.selectedTaskId === taskId;
      if (!isSelected && fromOther) {
        item.unreadCount = (item.unreadCount || 0) + 1;
        state.chatList.totalUnread = (state.chatList.totalUnread || 0) + 1;
      }

      // Remove from current position and push to front
      state.chatList.items = [item, ...items.filter((_, i) => i !== idx)];
    },

    /** Optimistically reset unread count for a task when user opens the chat. */
    taskChatListMarkRead(state, action) {
      const { taskId } = action.payload || {};
      if (!taskId) return;
      const item = state.chatList.items.find((i) => i.taskId === taskId);
      if (item && item.unreadCount > 0) {
        state.chatList.totalUnread = Math.max(0, (state.chatList.totalUnread || 0) - item.unreadCount);
        item.unreadCount = 0;
      }
    },
  },

  extraReducers: (b) => {
    b.addCase(fetchTaskChatMessages.pending, () => {})
      .addCase(fetchTaskChatMessages.fulfilled, (state, a) => {
        const {
          taskId,
          messages,
          memberIds,
          memberCount,
          mentionMembers,
          totalMessages,
          hasMore,
        } = a.payload;

        state.byTaskId[taskId] = {
          messages: messages || [],
          memberIds: memberIds || [],
          memberCount: memberCount || 0,
          mentionMembers: mentionMembers || [],
          totalMessages,
          hasMore,
        };
      })
      .addCase(fetchTaskChatMessages.rejected, () => {})
      .addCase(fetchOlderTaskChatMessages.fulfilled, (state, a) => {
        const {
          taskId,
          messages: incoming,
          hasMore,
          totalMessages,
          memberIds,
          memberCount,
          mentionMembers,
        } = a.payload;
        const prev = state.byTaskId[taskId] || {};
        const prevList = prev.messages || [];
        const merged = [...(incoming || []), ...prevList];
        state.byTaskId[taskId] = {
          ...prev,
          messages: merged,
          hasMore,
          totalMessages,
          ...(memberIds ? { memberIds } : {}),
          ...(memberCount != null ? { memberCount } : {}),
          ...(mentionMembers ? { mentionMembers } : {}),
        };
      })
      .addCase(sendTaskChatMessage.fulfilled, (state, a) => {
        const { taskId, message } = a.payload;
        if (!message) return;

        const prev = state.byTaskId[taskId] || {};
        state.byTaskId[taskId] = {
          messages: prev.messages || [],
          memberIds: prev.memberIds || [],
          memberCount: prev.memberCount || 0,
          mentionMembers: prev.mentionMembers || [],
        };

        const list = [...(state.byTaskId[taskId].messages || [])];
        const i = list.findIndex((x) => String(x.id) === String(message.id));

        if (i !== -1) {
          list[i] = message;
        } else {
          list.push(message);
        }

        state.byTaskId[taskId].messages = list;
      })
      .addCase(markTaskChatReadThunk.fulfilled, (state, a) => {
        const { taskId, readerId, readAt, viewerId } = a.payload || {};
        mutateTaskChatReadReceipt(state, { taskId, readerId, readAt, viewerId });
        // Also clear the unread badge in the chat list
        if (taskId) {
          const item = state.chatList.items.find((i) => i.taskId === taskId);
          if (item && item.unreadCount > 0) {
            state.chatList.totalUnread = Math.max(0, (state.chatList.totalUnread || 0) - item.unreadCount);
            item.unreadCount = 0;
          }
        }
      })
      .addCase(fetchTaskChatList.pending, (state) => {
        state.chatList.loading = true;
        state.chatList.error = null;
      })
      .addCase(fetchTaskChatList.fulfilled, (state, a) => {
        state.chatList.loading = false;
        state.chatList.items = a.payload?.data || [];
        state.chatList.pagination = a.payload?.pagination || state.chatList.pagination;
        state.chatList.totalUnread = a.payload?.totalUnread ?? recalcTotalUnread(a.payload?.data);
        state.chatList.search = a.meta?.arg?.search || "";
      })
      .addCase(fetchTaskChatList.rejected, (state, a) => {
        state.chatList.loading = false;
        state.chatList.error = a.payload || "Failed to load task chats";
      })
      .addCase(loadMoreTaskChatList.pending, (state) => {
        state.chatList.loadingMore = true;
      })
      .addCase(loadMoreTaskChatList.fulfilled, (state, a) => {
        state.chatList.loadingMore = false;
        const incoming = a.payload?.data || [];
        const existingIds = new Set(state.chatList.items.map((i) => i.taskId));
        const deduped = incoming.filter((item) => !existingIds.has(item.taskId));
        state.chatList.items = [...state.chatList.items, ...deduped];
        state.chatList.pagination = a.payload?.pagination || state.chatList.pagination;
        state.chatList.totalUnread = a.payload?.totalUnread ?? state.chatList.totalUnread;
      })
      .addCase(loadMoreTaskChatList.rejected, (state) => {
        state.chatList.loadingMore = false;
      });
  },
});

export const {
  clearTaskChat,
  taskChatSocketEnvelope,
  taskChatReadReceiptBatch,
  setSelectedTaskChatId,
  taskChatListSocketUpdate,
  taskChatListMarkRead,
} = slice.actions;

export default slice.reducer;
